/**
 * Centralised icon resolution for workflow actions.
 *
 * Workflow actions (approve, cancel, mark_paid, reject, …) surface in
 * many places - the action menu on detail pages, the pending approvals
 * list, the confirmation dialogs, and any future surfaces a tenant
 * builds on top of `apps.workflows`. This module is the single source
 * of truth for "which icon represents this action" so those surfaces
 * stay visually consistent without each one re-inventing a mapping.
 *
 * Resolution order:
 *   1. `action.icon` - explicit Lucide icon name set on the backend
 *      `WorkflowAction` (no admin UI to set it yet, but the schema is
 *      already plumbed through - `backend/apps/workflows/models/
 *      definition.py:201`).
 *   2. `action.ui_hints.icon` - runtime override in the JSON hints.
 *   3. Keyword-token fallback - scans `action.key` and `action.label`
 *      for common verbs (approve, cancel, mark paid, submit, reject,
 *      request info, activate, resolve, …) and picks a sensible icon.
 *   4. `null` - render no icon rather than guess wrong.
 *
 * Icons are a curated Lucide subset so Vite can still tree-shake the
 * library. Adding a new keyword = add one line to `KEYWORD_RULES`.
 */

import {
  ArrowLeftRight,
  ArrowRight,
  Check,
  CheckCheck,
  Clock,
  HelpingHand,
  MessageSquareText,
  Pause,
  Power,
  RefreshCw,
  RotateCcw,
  Send,
  ShieldCheck,
  Trash2,
  Undo2,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { LucideIconByName } from '@/shared/components/ui/lucide-icon-by-name'

/**
 * Curated icon registry. Keys are the icon names that may be stored on
 * `WorkflowAction.icon` or in `ui_hints.icon`. Lookup is case-insensitive
 * and treats `-`, `_`, ` ` as separators, so `"check-circle-2"`,
 * `"CheckCircle2"`, `"check_circle_2"` all resolve to the same icon.
 */
const ICON_REGISTRY: Record<string, LucideIcon> = {
  arrowleftright: ArrowLeftRight,
  arrowright: ArrowRight,
  check: Check,
  checkcheck: CheckCheck,
  clock: Clock,
  helpinghand: HelpingHand,
  messagesquaretext: MessageSquareText,
  pause: Pause,
  power: Power,
  refreshcw: RefreshCw,
  rotateccw: RotateCcw,
  send: Send,
  shieldcheck: ShieldCheck,
  trash: Trash2,
  trash2: Trash2,
  undo2: Undo2,
  x: X,
  zap: Zap,
}

/**
 * Keyword-based fallback rules. Ordered by specificity - multi-word
 * phrases (e.g. `mark paid`) come before single words (`paid`) so the
 * more precise match wins.
 *
 * Each pattern matches against the normalised action key/label, which
 * has separators collapsed to single spaces and is lower-cased.
 */
const KEYWORD_RULES: ReadonlyArray<readonly [RegExp, LucideIcon]> = [
  // "mark as …" covers a family of confirmatory actions: mark_paid,
  // mark_reviewed, mark_complete, mark_shipped, … The plain check
  // reads as "confirmed" regardless of what's being marked.
  [/\bmark(\s*as)?\b/, Check],
  [/\bapprove|accept|confirm|verify|validate\b/, Check],
  [/\bpaid\b|\bpay(ment)?\b/, Check],
  [/\breject|deny|decline|refuse\b/, X],
  [/\bcancel|abort|void\b/, X],
  [/\brequest\s*(info|information|more)\b|\bask\b/, MessageSquareText],
  [/\bsubmit|send\b/, Send],
  [/\bactivate|enable|start|go\s*live\b/, Zap],
  [/\bdeactivate|disable\b/, Power],
  [/\bexpire(d)?\b/, Clock],
  [/\bresolve|complete|finish|close\b/, CheckCheck],
  [/\breopen|restart\b/, RotateCcw],
  [/\bdelete|remove\b/, Trash2],
  [/\boverride\b/, ShieldCheck],
  [/\bretry|renew|extend\b/, RefreshCw],
  [/\breturn|back|revert\b/, Undo2],
  [/\btransfer|reassign\b/, ArrowLeftRight],
  [/\bhold|pause\b/, Pause],
  [/\bhelp\b/, HelpingHand],
]

/** Minimal shape we need from a WorkflowAction. Callers can pass the
 *  full action or any subset - every field is optional. */
export interface ActionIconSource {
  key?: string | null
  label?: string | null
  icon?: string | null
  ui_hints?: Record<string, unknown> | null
}

function normaliseIconName(raw: string | null | undefined): string | null {
  if (!raw) return null
  return raw.replace(/[-_\s]+/g, '').toLowerCase() || null
}

function normaliseTokenText(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase()
}

function lookupRegistry(name: string | null | undefined): LucideIcon | null {
  const key = normaliseIconName(name)
  return key ? ICON_REGISTRY[key] ?? null : null
}

function matchKeyword(action: ActionIconSource): LucideIcon | null {
  const haystack = [
    normaliseTokenText(action.key),
    normaliseTokenText(action.label),
  ]
    .filter(Boolean)
    .join(' ')
  if (!haystack) return null
  for (const [pattern, icon] of KEYWORD_RULES) {
    if (pattern.test(haystack)) return icon
  }
  return null
}

/**
 * Returns the best Lucide icon for a workflow action, or `null` when
 * no confident match exists. Callers that still want a visual anchor
 * can pass the result through `?? ArrowRight` at the call site.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function resolveActionIcon(
  action: ActionIconSource,
): LucideIcon | null {
  const explicit = lookupRegistry(action.icon)
  if (explicit) return explicit

  const hintIcon = action.ui_hints?.['icon']
  if (typeof hintIcon === 'string') {
    const hinted = lookupRegistry(hintIcon)
    if (hinted) return hinted
  }

  return matchKeyword(action)
}

interface WorkflowActionIconProps {
  action: ActionIconSource
  /** Optional icon used when resolution finds no match. */
  fallback?: LucideIcon | null
  className?: string
}

function getDynamicIconName(action: ActionIconSource): string | null {
  const raw = action.icon || (typeof action.ui_hints?.['icon'] === 'string' ? (action.ui_hints['icon'] as string) : null)
  if (!raw) return null
  // No validity pre-check: the icon set is lazy, so LucideIconByName resolves an
  // unknown name to its fallback (null here) instead.
  return raw.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/[_\s]+/g, '-').toLowerCase()
}

export function WorkflowActionIcon({
  action,
  fallback = null,
  className,
}: WorkflowActionIconProps) {
  const Icon = resolveActionIcon(action) ?? fallback
  if (Icon) {
    // eslint-disable-next-line react-hooks/static-components
    return <Icon className={className} aria-hidden="true" />
  }
  const dynamicName = getDynamicIconName(action)
  if (dynamicName) {
    return <LucideIconByName name={dynamicName} className={className} aria-hidden="true" />
  }
  return null
}
